/**
 * useDraggableRows — drag-and-drop row reordering for Ant Design Table.
 *
 * Uses native HTML5 drag-and-drop (no external library). Returns:
 *   - `dragHandleColumn`: a column definition to prepend to your columns array
 *     (renders a small "grip" icon you can grab to drag the row).
 *   - `onRow`: prop to spread on <Table> so each TR carries the drag handlers.
 *
 * The hook just shuffles the input array via setRows. The form/save layer is
 * responsible for reassigning sortOrder values (the existing forms already
 * do this on save, so no extra bookkeeping is needed).
 */

import React, { useCallback, useState } from 'react';
import { HolderOutlined } from '@ant-design/icons';

const DRAG_MIME = 'application/x-row-index';

export function useDraggableRows<T>(rows: T[], setRows: (rows: T[]) => void) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.dataTransfer.setData(DRAG_MIME, String(index));
    e.dataTransfer.effectAllowed = 'move';
    setDragIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault(); // required to allow drop
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetIndex: number) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData(DRAG_MIME);
      const sourceIndex = raw ? parseInt(raw, 10) : NaN;
      setDragIndex(null);
      setOverIndex(null);
      if (Number.isNaN(sourceIndex) || sourceIndex === targetIndex) return;
      const next = [...rows];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      setRows(next);
    },
    [rows, setRows]
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
  }, []);

  const dragHandleColumn = {
    title: '',
    key: '__dragHandle',
    width: 30,
    align: 'center' as const,
    render: () => (
      <HolderOutlined
        style={{ cursor: 'grab', color: '#999' }}
        title="Drag to reorder"
      />
    ),
  };

  const onRow = (_: T, index?: number) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => index != null && handleDragStart(e, index),
    onDragOver: (e: React.DragEvent) => index != null && handleDragOver(e, index),
    onDragLeave: handleDragLeave,
    onDrop: (e: React.DragEvent) => index != null && handleDrop(e, index),
    onDragEnd: handleDragEnd,
    style: {
      cursor: 'move',
      // Visual cue for the row currently being dragged + the hover target
      ...(dragIndex === index ? { opacity: 0.4 } : {}),
      ...(overIndex === index && dragIndex !== index
        ? { borderTop: '2px solid #1890ff' }
        : {}),
    } as React.CSSProperties,
  });

  return { onRow, dragHandleColumn };
}
